// ── UPLOAD A YIELD MAP FROM ANY MONITOR ──────────────────────────────────────
//
// Kevin, 2026-10-01: *"can we figure out a way to import yield data from any yield monitor
// through a manual upload?"*, *"We need to be able to accept precision planting, Trimble, ag
// leader, case ih, etc"*, and on where it belongs: *"I agree it should be in field mapper and
// then machine data"*.
//
// It renders on Field Mapper → 🚜 Machine Data, under the farm already picked in the screen
// toolbar. That tab's empty state is the exact audience: measured 2026-10-01, **208 of 233
// CY2026 farms have no John Deere connection**, carrying 194,015 insured acres with 99% of them
// already boundaried — so today those growers land on "No John Deere machine data for this farm
// yet · use Connected Accounts to invite them", which is a dead end for a farm that does not run
// John Deere at all.
//
// It also renders BELOW the records when a farm IS connected, because a farm can run John Deere
// on the combine and something else on the planter.
//
// ⚠⚠ THE PANEL ASKS; IT NEVER ASSUMES. Nothing in a DBF states a unit, a moisture basis or a
// crop — 180 is a plausible bu/ac of corn AND a plausible dry-matter ton figure times ten — and
// the number ends up on a production report a grower signs. Every refusal here comes from
// `importGaps` in src/claims/yieldImport.js rather than from this file, so the screen and the
// write path cannot disagree about what is required.

import React, { useState, useCallback, useEffect, useRef } from "react";
import { YIELD_UNITS, MOISTURE_BASIS, MONITORS, suggestCropYear, cropYearConflict } from "./yieldImport";

// ── PROGRESS, AND ONLY WHAT IS ACTUALLY KNOWN ────────────────────────────────
// Kevin, 2026-10-01: *"also wire in a progress bar while uploading or something confirms the
// data was successfully imported or whether the import did not work"*.
//
// ⚠ TWO OF THE THREE PHASES HAVE NO PROGRESS TO REPORT, and that is the honest part. The
// UPLOAD knows its bytes (XMLHttpRequest reports them), so it gets a real bar. READING the file
// and IMPORTING it are single server round-trips that return when they return — a percentage
// there would be invented, and a bar creeping to 90% and sitting is a worse lie than one that
// says it cannot tell. Those get a moving indeterminate bar and an ELAPSED SECONDS counter,
// which is a fact: it tells the reader the thing is alive and how long it has been going.
function Bar({ pct, label, elapsed }) {
  const indeterminate = pct == null;
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#2d4a2d", marginBottom: 4 }}>
        <span style={{ fontWeight: 700 }}>{label}</span>
        <span style={{ fontVariantNumeric: "tabular-nums", color: "#5a6a5a" }}>
          {indeterminate ? (elapsed != null ? `${elapsed}s` : "") : `${pct}%`}
        </span>
      </div>
      <div style={{ height: 8, background: "#e8f0e6", borderRadius: 999, overflow: "hidden" }}>
        <div style={{
          height: "100%", borderRadius: 999, background: "#2d6a2d",
          width: indeterminate ? "35%" : `${Math.max(2, Math.min(100, pct))}%`,
          transition: indeterminate ? "none" : "width .2s linear",
          animation: indeterminate ? "yiSlide 1.1s ease-in-out infinite" : "none",
        }} />
      </div>
      {/* prefers-reduced-motion is honoured: the bar still shows, it simply stops sliding. */}
      <style>{`@keyframes yiSlide{0%{margin-left:0}50%{margin-left:65%}100%{margin-left:0}}
        @media (prefers-reduced-motion: reduce){ [style*="yiSlide"]{animation:none!important} }`}</style>
    </div>
  );
}

const card = { background: "#fff", border: "1px solid #dde8d8", borderRadius: 10, padding: 14 };
const label = { fontSize: 11, fontWeight: 800, color: "#5a6a5a", textTransform: "uppercase", letterSpacing: 0.3 };
const field = { width: "100%", padding: "7px 9px", border: "1px solid #cdd9cd", borderRadius: 7, fontSize: 13, fontFamily: "inherit" };

/**
 * YieldImportPanel
 *   farmerId  — the farm the Machine Data tab is already scoped to
 *   farmName  — for the heading only
 *   onDone    — called after a successful import, so the tab can refresh
 *   authedFetch / sbStorageUpload — passed in, because this file must not reach for the app's
 *                                   session itself: the Machine Data tab renders in the PORTAL
 *                                   too, and a component that resolves its own identity there
 *                                   is the shape check-home-safe exists to forbid.
 */
export default function YieldImportPanel({ farmerId, farmName, isMobile, authedFetch, uploadFile, onDone }) {
  const [stage, setStage] = useState("idle");      // idle | uploading | inspecting | ready | importing | done
  const [err, setErr] = useState(null);
  const [file, setFile] = useState(null);
  const [path, setPath] = useState(null);
  const [look, setLook] = useState(null);          // the inspect result
  // ⚠ THE CROP YEAR IS NOT DEFAULTED. It was pre-filled with the current year, so an old map
  // whose date was typed correctly would still have filed as THIS year unless somebody noticed
  // the box — a value nobody entered appearing as though they had. It is suggested from the
  // date below, which is a fact the operator supplies, and the suggestion says where it came
  // from.
  const [draft, setDraft] = useState({ crop: "", year: "",
                                       date: "", metric: "yield", column: "", unit: "",
                                       moisture: "", monitor: "" });
  const [yearTouched, setYearTouched] = useState(false);
  const [result, setResult] = useState(null);
  const [pct, setPct] = useState(null);            // upload %, or null where it cannot be known
  const [elapsed, setElapsed] = useState(0);       // seconds in the current phase
  const t0 = useRef(0);

  // The elapsed counter runs only while something is in flight. It is a FACT, not a forecast —
  // the point is to show the thing is alive during a phase whose length nobody can predict.
  const busy = stage === "uploading" || stage === "inspecting" || stage === "importing";
  useEffect(() => {
    if (!busy) { setElapsed(0); return undefined; }
    t0.current = Date.now();
    const id = setInterval(() => setElapsed(Math.round((Date.now() - t0.current) / 1000)), 500);
    return () => clearInterval(id);
  }, [busy, stage]);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  // The suggestion follows the DATE. It fills the box only while the operator has not typed a
  // year themselves — once they have, their answer stands and the conflict line below is what
  // tells them if it disagrees with the date.
  const yearHint = suggestCropYear({ operationDate: draft.date, crop: draft.crop, metric: draft.metric });
  useEffect(() => {
    if (yearTouched || !yearHint) return;
    setDraft((d) => (String(d.year) === String(yearHint.year) ? d : { ...d, year: String(yearHint.year) }));
  }, [yearHint && yearHint.year, yearTouched]);   // eslint-disable-line
  const clash = cropYearConflict({ operationDate: draft.date, crop: draft.crop,
                                   metric: draft.metric, cropYear: draft.year });

  const pick = useCallback(async (f) => {
    if (!f || !farmerId) return;
    setErr(null); setResult(null); setLook(null); setFile(f); setPct(0); setStage("uploading");
    try {
      // ⚠ CATEGORY FIRST, FARMER SECOND — `yield-maps/<farmer>/<file>`. That is the bucket's
      // own convention (its top level is signed / documents / added-land-pins /
      // acreage-filled / 578s) and it is what the storage RLS policy keys on: the farmer id
      // is path SEGMENT 2. The first cut wrote `<farmer>/yield-maps/...`, which puts the
      // farm where the policy expects a category — it would have been refused by RLS, or
      // worse, landed somewhere the policy does not scope.
      const safe = f.name.replace(/[^\w.-]+/g, "_");
      const key = `yield-maps/${farmerId}/${Date.now()}-${safe}`;
      await uploadFile(key, f, (p) => setPct(p));
      setPath(key);
      setPct(null);                                 // the server's turn; nothing to measure
      setStage("inspecting");
      const r = await authedFetch(`/api/market?fn=jd-yield-inspect&farmer=${encodeURIComponent(farmerId)}`
        + `&path=${encodeURIComponent(key)}&metric=${encodeURIComponent(draft.metric)}`);
      const j = await r.json();
      if (!r.ok || j.error) throw new Error(j.error || `inspect failed (${r.status})`);
      setLook(j);
      // A Climate FieldView export states its own crop, weight, area and date — there is no
      // column to pick, so the draft is left alone and the operator reviews what was read.
      if (j.format === "fieldview") { setStage("ready"); return; }
      // ⚠ PRE-SELECT ONLY WHAT THE FILE OR A PAST IMPORT ACTUALLY SUPPORTS. A suggested column
      // is filled in; its unit and basis are filled in ONLY where a previous import of the same
      // column answered them. Nothing else is guessed — see yieldImport.js.
      const best = (j.columns || []).find((c) => c.suggested);
      setDraft((d) => ({ ...d,
        column: best ? best.name : "",
        unit: best && best.lastUnit ? best.lastUnit : d.unit,
        moisture: best && best.lastMoistureBasis ? best.lastMoistureBasis : d.moisture }));
      setStage("ready");
    } catch (e) { setErr(e.message); setPct(null); setStage("idle"); }
  }, [farmerId, uploadFile, authedFetch, draft.metric]);

  const doImport = useCallback(async () => {
    setErr(null); setStage("importing");
    try {
      const q = new URLSearchParams({
        fn: "jd-yield-import", farmer: farmerId, path,
        crop: draft.crop, year: draft.year, date: draft.date, metric: draft.metric,
        column: draft.column, unit: draft.unit, moisture: draft.moisture, monitor: draft.monitor,
      });
      const r = await authedFetch(`/api/market?${q.toString()}`);
      const j = await r.json();
      if (!r.ok || j.error) {
        // The write path returns WHICH answers are missing, not just that the request was bad.
        const need = (j.still_needed || []).map((g) => g.say).join("; ");
        throw new Error(need ? `still needed — ${need}` : (j.error || `import failed (${r.status})`));
      }
      setResult(j); setStage("done");
      if (onDone) onDone();
    } catch (e) { setErr(e.message); setStage("ready"); }
  }, [farmerId, path, draft, authedFetch, onDone]);

  const doImportFieldView = useCallback(async () => {
    setErr(null); setStage("importing");
    try {
      const q = new URLSearchParams({ fn: "jd-yield-import", farmer: farmerId, path, format: "fieldview" });
      const r = await authedFetch(`/api/market?${q.toString()}`);
      const j = await r.json();
      if (!r.ok || j.error || !j.ok) {
        const why = (j.fields || []).filter((f) => f.refused).map((f) => `${f.field}: ${f.refused}`).join("; ");
        throw new Error(j.error || why || `import failed (${r.status})`);
      }
      setResult(j); setStage("done");
      if (onDone) onDone();
    } catch (e) { setErr(e.message); setStage("ready"); }
  }, [farmerId, path, authedFetch, onDone]);

  if (!farmerId) return null;

  const isFv = !!(look && look.format === "fieldview");
  const sel = (look && (look.columns || []).find((c) => c.name === draft.column)) || null;
  // Mirrors importGaps — but the SERVER decides; this only disables the button so the operator
  // is not invited to press something that will be refused.
  // ⚠ A HARD CROP-YEAR CONFLICT BLOCKS THE BUTTON. `importGaps` refuses it server-side too —
  // this only stops the operator pressing something that will be refused.
  const incomplete = !draft.crop || !draft.year || !draft.date || !draft.column || !draft.unit
    || (draft.metric === "yield" && !draft.moisture)
    || !!(clash && clash.severity === "refuse");

  return (
    <div style={{ ...card, marginTop: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: "#1a2e1a", marginBottom: 2 }}>
        Upload a yield map
      </div>
      <div style={{ fontSize: 12.5, color: "#5a6a5a", marginBottom: 10, lineHeight: 1.5 }}>
        A zipped shapefile from any monitor — Precision Planting, Trimble, Ag Leader, Case IH,
        John Deere or another. {farmName ? <>For <b>{farmName}</b>.</> : null}{" "}
        {/* ⚠ SAID UP FRONT, because it is the one thing the file cannot tell us and the one
            thing that changes the number. */}
        You will be asked which column holds the yield and what unit it is in — a shapefile
        states neither, and guessing either would put a wrong figure on a production report.
      </div>

      {stage === "idle" || stage === "uploading" || stage === "inspecting" ? (
        <label style={{ display: "inline-block", cursor: stage === "idle" ? "pointer" : "default",
          border: "1px dashed #9ab89a", borderRadius: 9, padding: "14px 18px", background: "#f6faf6",
          fontSize: 13, color: "#2d4a2d", fontWeight: 700 }}>
          <input type="file" accept=".zip" style={{ display: "none" }} disabled={stage !== "idle"}
            onChange={(e) => pick(e.target.files && e.target.files[0])} />
          {stage === "uploading" ? "Uploading…" : stage === "inspecting" ? "Reading the file…"
            : "Choose a .zip — a shapefile or a Climate FieldView export"}
        </label>
      ) : null}

      {/* ⚠ EVERY PHASE SHOWS SOMETHING MOVING. A file that takes 40 seconds to upload with no
          indicator reads as a broken button, and the operator presses it again. */}
      {stage === "uploading" ? (
        <Bar pct={pct} elapsed={elapsed}
          label={`Uploading ${file ? file.name : "the file"}${file && file.size ? ` · ${(file.size / 1e6).toFixed(1)} MB` : ""}`} />
      ) : null}
      {stage === "inspecting" ? (
        <Bar pct={null} elapsed={elapsed} label="Reading the file — columns, points and which fields it covers" />
      ) : null}
      {stage === "importing" ? (
        <Bar pct={null} elapsed={elapsed} label="Importing — binning the points and matching them to insured fields" />
      ) : null}

      {/* ⚠ A FAILURE MUST BE AS LOUD AS A SUCCESS, AND MUST SAY WHAT TO DO. Kevin asked for
          confirmation of "whether the import did not work" — so this is a heading, not a tint:
          an operator who scrolls past a pale line and assumes it landed is the failure mode. */}
      {err ? (
        <div style={{ marginTop: 10, fontSize: 13, color: "#8a1c1c", background: "#fdecec",
          border: "1px solid #a3333355", borderRadius: 9, padding: "10px 12px", lineHeight: 1.55 }}>
          <div style={{ fontWeight: 800, marginBottom: 2 }}>✕ Not imported</div>
          <div>{err}</div>
          <div style={{ fontSize: 11.5, color: "#7a4a4a", marginTop: 5 }}>
            Nothing was stored and nothing was changed. Fix the point above and try again — or
            pick a different file.
          </div>
        </div>
      ) : null}

      {/* ⚠ A FIELDVIEW EXPORT ASKS NOTHING, so what it SHOWS has to carry the weight: how each
          field was read, whether its points tied to the monitor's own load totals, any second
          recording of the same combine that was folded in, and where the acres will land. */}
      {isFv && stage !== "done" ? (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ fontSize: 12.5, color: "#2d4a2d", background: "#f4f8f3", borderRadius: 8, padding: "8px 10px", lineHeight: 1.55 }}>
            <b>{file ? file.name : "file"}</b> is a <b>Climate FieldView</b> export —{" "}
            {(look.fields || []).length} field{(look.fields || []).length === 1 ? "" : "s"} of harvest data.
            Bushels are the monitor's weighed pounds at the crop's standard test weight, as harvested
            (not moisture-adjusted) — never the monitor's own bushel figure, which uses whatever crop
            the operator had set.
            {look.skipped ? <> {look.skipped} other file{look.skipped === 1 ? " is" : "s are"} not harvest data and {look.skipped === 1 ? "is" : "are"} left out.</> : null}
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
              <thead>
                <tr style={{ textAlign: "left", color: "#5a6a5a" }}>
                  {["FieldView field", "Crop", "Date", "Acres", "Bu", "Bu/ac", "Lands on"].map((h) => (
                    <th key={h} style={{ padding: "4px 6px", borderBottom: "1px solid #dde8d8", fontWeight: 800 }}>{h}</th>))}
                </tr>
              </thead>
              <tbody>
                {(look.fields || []).map((f) => {
                  const pl = (look.placement || []).find((p) => p.field === f.field);
                  const dup = (f.recordings || []).find((r) => r.duplicate_of);
                  const tied = (f.recordings || []).some((r) => r.tied_to_load_list === true);
                  return (
                    <tr key={f.field} style={{ borderBottom: "1px solid #eef3ec", verticalAlign: "top" }}>
                      <td style={{ padding: "5px 6px", fontWeight: 700 }}>{f.field}</td>
                      {f.refused ? (
                        <td colSpan={6} style={{ padding: "5px 6px", color: "#a33" }}>✕ Not importable — {f.refused}</td>
                      ) : (<>
                        <td style={{ padding: "5px 6px" }}>{f.crop}</td>
                        <td style={{ padding: "5px 6px" }}>{f.date}</td>
                        <td style={{ padding: "5px 6px", fontVariantNumeric: "tabular-nums" }}>{Number(f.ac).toFixed(1)}</td>
                        <td style={{ padding: "5px 6px", fontVariantNumeric: "tabular-nums" }}>{Number(f.bu).toLocaleString()}</td>
                        <td style={{ padding: "5px 6px", fontVariantNumeric: "tabular-nums" }}>{Number(f.bu_per_ac).toFixed(1)}</td>
                        <td style={{ padding: "5px 6px", lineHeight: 1.45 }}>
                          {pl && pl.this_farm.length ? <div>This farm: {pl.this_farm.map((x) => `${x.ftf} (${x.ac} ac)`).join(", ")}</div> : null}
                          {pl && pl.another_insured_ac > 0 ? <div style={{ color: "#8a6d1f" }}>Another insured grower's field: {pl.another_insured_ac} ac (custom work — credited to that grower)</div> : null}
                          {pl && pl.no_insured_clu_ac > 0 ? <div style={{ color: "#5a6a5a" }}>No insured field: {pl.no_insured_clu_ac} ac (not counted)</div> : null}
                          <div style={{ fontSize: 11, color: "#5a6a5a" }}>
                            {tied ? "✓ ties to the monitor's load totals" : "no load totals in the file to tie to"}
                            {dup ? ` · a second recording of the same combine (clock ${dup.duplicate_of.clock_offset_hours} h off) was merged, keeping only passes the first missed` : ""}
                          </div>
                        </td>
                      </>)}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div>
            <button onClick={doImportFieldView}
              disabled={stage === "importing" || !(look.fields || []).some((f) => !f.refused)}
              style={{ padding: "9px 16px", borderRadius: 8, border: "none", fontSize: 13, fontWeight: 800,
                background: "#2d4a2d", color: "#fff", cursor: "pointer" }}>
              {stage === "importing" ? "Importing…" : `Import ${(look.fields || []).filter((f) => !f.refused).length} field${(look.fields || []).filter((f) => !f.refused).length === 1 ? "" : "s"}`}
            </button>
            <span style={{ fontSize: 11.5, color: "#5a6a5a", marginLeft: 10 }}>
              Only insured fields are counted. Importing this export again replaces these fields rather than adding to them.
            </span>
          </div>
        </div>
      ) : null}

      {look && !isFv && stage !== "done" ? (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
          {/* WHAT THE FILE IS, before anything is chosen. */}
          <div style={{ fontSize: 12.5, color: "#2d4a2d", background: "#f4f8f3", borderRadius: 8, padding: "8px 10px" }}>
            <b>{file ? file.name : "file"}</b> — {Number(look.points).toLocaleString()} points,{" "}
            {(look.columns || []).length} columns
            {look.rows_match === false ? (
              // ⚠ A SHAPEFILE AND ITS DBF HOLD ONE ROW PER POINT. When they disagree the FILE is
              // wrong, not the import, and binning would silently take the smaller.
              <div style={{ color: "#a33", marginTop: 4 }}>
                ⚠ {Number(look.points).toLocaleString()} points but {Number(look.records).toLocaleString()} data
                rows — they should match. Check the export before importing this.
              </div>
            ) : null}
            {look.on_earth === false ? (
              <div style={{ color: "#a33", marginTop: 4 }}>
                ⚠ The coordinates are not latitude/longitude — this looks like a projected export.
                Re-export it in WGS84 / lat-lon.
              </div>
            ) : null}
            {look.overlaps_insured_fields && look.overlaps_insured_fields.length ? (
              <div style={{ marginTop: 4 }}>
                Covers <b>{look.overlaps_insured_fields.length}</b> insured field
                {look.overlaps_insured_fields.length === 1 ? "" : "s"} on this farm
                {" — "}{[...new Set(look.overlaps_insured_fields.map((f) => f.county).filter(Boolean))].join(", ")}
              </div>
            ) : look.on_earth !== false ? (
              // Not a refusal: the ground may simply be unmapped, or insured under another year.
              <div style={{ color: "#8a6d1f", marginTop: 4 }}>
                No insured field of this farm sits inside this file's area. It can still be
                imported — but check it is the right farm and the right crop year.
              </div>
            ) : null}
          </div>

          <div style={{ display: "grid", gap: 10,
            gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0,1fr))" }}>
            <div>
              <div style={label}>Pass</div>
              <select style={field} value={draft.metric} onChange={(e) => set("metric", e.target.value)}>
                <option value="yield">Harvest (yield)</option>
                <option value="seed_rate">Planting (seeding rate)</option>
              </select>
            </div>
            <div>
              <div style={label}>Crop</div>
              <input style={field} value={draft.crop} placeholder="CORN"
                onChange={(e) => set("crop", e.target.value.toUpperCase())} />
            </div>
            <div>
              <div style={label}>Crop year</div>
              <input style={{ ...field, borderColor: clash && clash.severity === "refuse" ? "#a33" : field.borderColor }}
                value={draft.year} placeholder="from the date"
                onChange={(e) => { setYearTouched(true); set("year", e.target.value); }} />
              {/* ⚠ THE DERIVATION IS SHOWN, so the operator can see WHY it says what it says
                  rather than finding a number already in the box. */}
              {yearHint && !clash ? (
                <div style={{ fontSize: 11.5, color: "#5a6a5a", marginTop: 4 }}>
                  From the date — {yearHint.why}.
                </div>
              ) : null}
              {clash ? (
                <div style={{ fontSize: 11.5, marginTop: 4, fontWeight: 700,
                  color: clash.severity === "refuse" ? "#a33" : "#8a6d1f" }}>
                  {clash.severity === "refuse" ? "⚠ " : ""}{clash.say}
                </div>
              ) : null}
            </div>
            <div>
              <div style={label}>Date of the pass</div>
              <input style={field} type="date" value={draft.date} onChange={(e) => set("date", e.target.value)} />
            </div>
            <div>
              <div style={label}>Monitor</div>
              <input style={field} list="yi-monitors" value={draft.monitor} placeholder="Trimble"
                onChange={(e) => set("monitor", e.target.value)} />
              {/* A datalist, never a closed <select>: the list is a convenience and `etc` was
                  Kevin's own word. Anything may be typed. */}
              <datalist id="yi-monitors">
                {MONITORS.map((m) => <option key={m} value={m} />)}
              </datalist>
            </div>
            <div>
              <div style={label}>Unit</div>
              <select style={field} value={draft.unit} onChange={(e) => set("unit", e.target.value)}>
                <option value="">— choose —</option>
                {YIELD_UNITS.map((u) => <option key={u.id} value={u.id}>{u.id} · {u.label}</option>)}
              </select>
            </div>
          </div>

          {/* THE COLUMN — the file's own, with why each is or is not suggested. */}
          <div>
            <div style={label}>Which column holds the measurement</div>
            <select style={field} value={draft.column} onChange={(e) => set("column", e.target.value)}>
              <option value="">— choose —</option>
              {(look.columns || []).map((c) => (
                <option key={c.name} value={c.name}>
                  {c.suggested ? "★ " : "   "}{c.name} — {c.why}
                </option>
              ))}
            </select>
            {sel && sel.suggested === false ? (
              <div style={{ fontSize: 11.5, color: "#8a6d1f", marginTop: 4 }}>
                This column is not suggested: {sel.why}. Pick it only if you know this file.
              </div>
            ) : null}
            {sel && sel.lastUnit ? (
              <div style={{ fontSize: 11.5, color: "#5a6a5a", marginTop: 4 }}>
                Last time this column was imported it was <b>{sel.lastUnit}</b>
                {sel.lastMoistureBasis ? <>, {sel.lastMoistureBasis === "market" ? "already moisture-adjusted" : "as harvested"}</> : null}.
                Confirm it still is.
              </div>
            ) : null}
          </div>

          {/* ⚠ MOISTURE IS A HARVEST QUESTION ONLY. Asking it of a planting pass is a question
              with no true answer, and an operator who has to invent one will. */}
          {draft.metric === "yield" ? (
            <div>
              <div style={label}>Is that column already moisture-adjusted?</div>
              <select style={field} value={draft.moisture} onChange={(e) => set("moisture", e.target.value)}>
                <option value="">— choose —</option>
                {MOISTURE_BASIS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
              <div style={{ fontSize: 11.5, color: "#8a6d1f", marginTop: 4 }}>
                Wet bushels reported as dry overstate production. If you are not sure, ask the
                grower before importing — there is deliberately no "unknown" here.
              </div>
            </div>
          ) : null}

          <div>
            <button onClick={doImport} disabled={incomplete || stage === "importing"}
              style={{ padding: "9px 16px", borderRadius: 8, border: "none", fontSize: 13, fontWeight: 800,
                background: incomplete ? "#cdd9cd" : "#2d4a2d", color: "#fff",
                cursor: incomplete ? "default" : "pointer" }}>
              {stage === "importing" ? "Importing…" : "Import this yield map"}
            </button>
            {incomplete ? (
              <span style={{ fontSize: 12, color: "#8a6d1f", marginLeft: 10 }}>
                Answer every field above — nothing here is assumed.
              </span>
            ) : null}
          </div>
        </div>
      ) : null}

      {stage === "done" && result && result.format === "fieldview" ? (
        <div style={{ marginTop: 12, fontSize: 13, color: "#2d6a2d", background: "#eef7ec",
          border: "1px solid #2d6a2d22", borderRadius: 8, padding: "10px 12px", lineHeight: 1.6 }}>
          <div style={{ fontWeight: 800, fontSize: 13.5, marginBottom: 2 }}>
            ✓ Imported {result.total.fields} field{result.total.fields === 1 ? "" : "s"} — {Number(result.total.ac).toFixed(1)} ac,{" "}
            {Number(result.total.bu).toLocaleString()} bu
          </div>
          {(result.fields || []).map((f) => (
            <div key={f.field} style={{ fontSize: 12, color: f.refused ? "#a33" : "#2d4a2d" }}>
              {f.field}: {f.refused ? `✕ not imported — ${f.refused}`
                : `${f.crop} CY${f.crop_year} · ${f.date} · ${Number(f.ac).toFixed(1)} ac · ${Number(f.bu_per_ac).toFixed(1)} bu/ac`}
            </div>
          ))}
          <div style={{ fontSize: 11.5, color: "#5a6a5a", marginTop: 4 }}>
            Recorded as a hand upload from Climate FieldView, bu/ac as harvested. Field Progress and
            Machine Data pick these up on their next load.
          </div>
          <button onClick={() => { setStage("idle"); setLook(null); setFile(null); setPath(null); setResult(null); }}
            style={{ marginTop: 8, background: "none", border: "1px solid #cdd9cd", borderRadius: 7,
              padding: "5px 11px", fontSize: 12, color: "#2d4a2d", cursor: "pointer" }}>
            Upload another
          </button>
        </div>
      ) : null}

      {stage === "done" && result && result.format !== "fieldview" ? (
        <div style={{ marginTop: 12, fontSize: 13, color: "#2d6a2d", background: "#eef7ec",
          border: "1px solid #2d6a2d22", borderRadius: 8, padding: "10px 12px", lineHeight: 1.6 }}>
          {/* ⚠ WHAT LANDED, IN FIGURES. "Imported ✓" alone is a claim; the mean, the unit and
              the point count are the evidence, and they are what a reader checks it against. */}
          <div style={{ fontWeight: 800, fontSize: 13.5, marginBottom: 2 }}>✓ Imported</div>
          {result.stored && result.stored.bu_mean != null ? (
            <>Mean <b>{Math.round(result.stored.bu_mean).toLocaleString()} {result.stored.unit || ""}</b> over{" "}
              {Number(result.stored.points || 0).toLocaleString()} points
              {result.stored.cells_filled ? <>, {Number(result.stored.cells_filled).toLocaleString()} grid cells</> : null}.</>
          ) : (
            // A store that returned no mean is NOT a success to report as one.
            <>The file was read, but no measurement came back — check the column and the unit.</>
          )}
          <div style={{ fontSize: 11.5, color: "#5a6a5a", marginTop: 4 }}>
            Recorded as a hand upload from {result.provenance && result.provenance.monitor
              ? result.provenance.monitor : "an unnamed monitor"}, read from{" "}
            <b>{result.provenance && result.provenance.rate_column}</b> in{" "}
            <b>{result.provenance && result.provenance.unit}</b>. Re-importing the same file
            replaces this rather than adding to it.
          </div>
          <button onClick={() => { setStage("idle"); setLook(null); setFile(null); setPath(null); setResult(null); }}
            style={{ marginTop: 8, background: "none", border: "1px solid #cdd9cd", borderRadius: 7,
              padding: "5px 11px", fontSize: 12, color: "#2d4a2d", cursor: "pointer" }}>
            Upload another
          </button>
        </div>
      ) : null}
    </div>
  );
}
